import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileText,
  History,
  Loader2,
  Paperclip,
  Plus,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";
import { useAuth } from "../../../../../context/auth-context";
import RegisterDocumentSidePanel, {
  type RegisterDocumentForm,
} from "./RegisterDocumentSidePanel";

import DocumentoDetailSidePanel from "./DocumentoDetailSidePanel";

type SectionKey =
  | "visao-geral"
  | "a-vencer"
  | "vencidas"
  | "anexados"
  | "renovacoes"
  | "historico";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "semanal"
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OperacionalKey =
  | "ok"
  | "pendente_anexo"
  | "vencida"
  | "renovacao_pendente"
  | "aguardando_validacao"
  | "pendente"
  | "nao_exigido";

type OrigemSolicitacao =
  | "cliente"
  | "portal_cliente"
  | "email_cliente"
  | "fiscalizacao"
  | "contador"
  | "interno"
  | "outro";

type ProvidenciaStatus =
  | "nao_aplicavel"
  | "nao_iniciado"
  | "sem_agendamento"
  | "agendada"
  | "vistoria_tecnica_agendada"
  | "comparecimento_presencial"
  | "aguardando_documentos"
  | "aguardando_cliente"
  | "em_emissao"
  | "em_renovacao"
  | "emitido"
  | "concluida";

type ProvidenciaTipoAtendimento =
  | "nao_definido"
  | "online"
  | "email"
  | "presencial"
  | "vistoria_tecnica"
  | "contador"
  | "telefone"
  | "outro";

type AnexoFilter = "vinculado" | "pendente";

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  renovacaoPendente: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key: OperacionalKey;
  label: string;
  background: string;
  color: string;
  border: string;
};

type CertidaoItem = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;

  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload?: string | null;

  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;

  areaResponsavel: string;
  responsavelNome?: string | null;

  statusValidade: StatusValidade;

  documentoPossuido: boolean;
  origemSolicitacao: OrigemSolicitacao;
  clienteSolicitante: string | null;
  motivoExigencia: string | null;

  providenciaStatus: ProvidenciaStatus;
  providenciaAgendadaPara: string | null;
  providenciaTipoAtendimento: ProvidenciaTipoAtendimento;
  providenciaLocal: string | null;
  providenciaDocumentosNecessarios: string | null;
  providenciaObservacao: string | null;

  anexoDisponivel: boolean;
  arquivoVigenteId?: number | null;
  arquivoNome?: string | null;
  s3Key?: string | null;

  observacao?: string | null;

  versao: number;
  vigente: boolean;
  obsoleto: boolean;
  documentoSubstituidoId?: number | null;
  dataSubstituicao?: string | null;

  portais: PortalResumo;
};

type ApiDocumentoItem = {
  id: number | string;

  nome?: string | null;
  categoria?: string | null;
  orgaoEmissor?: string | null;
  orgao_emissor?: string | null;

  tipoValidade?: string | null;
  tipo_validade?: string | null;
  periodicidade?: string | null;

  areaResponsavel?: string | null;
  area_responsavel?: string | null;
  responsavelNome?: string | null;
  responsavel_nome?: string | null;

  arquivoVigenteId?: number | string | null;
  arquivo_vigente_id?: number | string | null;
  arquivoNome?: string | null;
  arquivo_nome?: string | null;
  s3Key?: string | null;
  s3_key?: string | null;

  dataEmissao?: string | null;
  data_emissao?: string | null;
  dataValidade?: string | null;
  data_validade?: string | null;
  dataUpload?: string | null;
  data_upload?: string | null;

  statusValidade?: string | null;
  status_validade?: string | null;
  anexoDisponivel?: boolean | null;
  anexo_disponivel?: boolean | null;

  documentoPossuido?: boolean | null;
  documento_possuido?: boolean | null;
  origemSolicitacao?: string | null;
  origem_solicitacao?: string | null;
  clienteSolicitante?: string | null;
  cliente_solicitante?: string | null;
  motivoExigencia?: string | null;
  motivo_exigencia?: string | null;

  providenciaStatus?: string | null;
  providencia_status?: string | null;
  providenciaAgendadaPara?: string | null;
  providencia_agendada_para?: string | null;
  providenciaTipoAtendimento?: string | null;
  providencia_tipo_atendimento?: string | null;
  providenciaLocal?: string | null;
  providencia_local?: string | null;
  providenciaDocumentosNecessarios?: string | null;
  providencia_documentos_necessarios?: string | null;
  providenciaObservacao?: string | null;
  providencia_observacao?: string | null;

  renovacaoStatus?: string | null;
  renovacao_status?: string | null;
  renovacaoAgendadaPara?: string | null;
  renovacao_agendada_para?: string | null;
  renovacaoTipoAtendimento?: string | null;
  renovacao_tipo_atendimento?: string | null;
  renovacaoLocal?: string | null;
  renovacao_local?: string | null;
  renovacaoDocumentosNecessarios?: string | null;
  renovacao_documentos_necessarios?: string | null;
  renovacaoObservacao?: string | null;
  renovacao_observacao?: string | null;

  emissaoStatus?: string | null;
  emissao_status?: string | null;
  emissaoAgendadaPara?: string | null;
  emissao_agendada_para?: string | null;
  emissaoTipoAtendimento?: string | null;
  emissao_tipo_atendimento?: string | null;
  emissaoLocal?: string | null;
  emissao_local?: string | null;
  emissaoDocumentosNecessarios?: string | null;
  emissao_documentos_necessarios?: string | null;
  emissaoObservacao?: string | null;
  emissao_observacao?: string | null;

  observacao?: string | null;
  descricao?: string | null;

  portais?: {
    totalExigem?: number | string | null;
    anexadosAtualizados?: number | string | null;
    pendenteAnexo?: number | string | null;
    renovacaoPendente?: number | string | null;
    emRenovacao?: number | string | null;
    vencidos?: number | string | null;
    aguardandoValidacao?: number | string | null;
  } | null;
};

type ApiDocumentosResponse = {
  ok?: boolean;
  items?: ApiDocumentoItem[];
  erro?: string;
};

type ApiPostDocumentoResponse = {
  ok?: boolean;
  documento?: {
    id?: number | string;
    [key: string]: any;
  };
  erro?: string;
};

type ApiPresignUploadResponse = {
  ok?: boolean;
  bucket?: string;
  key?: string;
  uploadUrl?: string;
  expiresIn?: number;
  mimeType?: string;
  originalName?: string;
  sizeBytes?: number;
  erro?: string;
};

type ApiRegistrarAnexoResponse = {
  ok?: boolean;
  arquivo?: any;
  erro?: string;
};

type ColumnFilters = {
  documento: string;
  categoria: string;
  orgaoEmissor: string;
  validade: string;
  regra: string;
  responsavel: string;
  statusValidade: string;
  operacional: string;
  anexo: string;
  providencia: string;
};

type CertidoesDocumentosLayoutProps = {
  section?: SectionKey;
};

const API_BASE = "https://api.logvi-havox.com";
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;
const API_PRESIGN_UPLOAD_URL = `${API_BASE}/template/governanca/documentos/anexos/presign-upload`;
const API_REGISTRAR_ANEXO_URL = `${API_BASE}/template/governanca/documentos/anexos/registrar`;

const BASE_PATH = "/governanca-qualidade-e-compliance/certidoes-e-documentos";
const DEPARTMENT_COLOR = "#2f3f59";




const sections: Array<{
  key: SectionKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}> = [
  {
    key: "visao-geral",
    label: "Visão geral",
    path: `${BASE_PATH}/visao-geral`,
    icon: <ShieldCheck size={14} />,
  },
  {
    key: "a-vencer",
    label: "A vencer",
    path: `${BASE_PATH}/a-vencer`,
    icon: <CalendarClock size={14} />,
  },
  {
    key: "vencidas",
    label: "Vencidas",
    path: `${BASE_PATH}/vencidas`,
    icon: <AlertTriangle size={14} />,
  },
  {
    key: "anexados",
    label: "Anexados",
    path: `${BASE_PATH}/anexados`,
    icon: <Paperclip size={14} />,
  },
  {
    key: "renovacoes",
    label: "Providências",
    path: `${BASE_PATH}/renovacoes`,
    icon: <Clock3 size={14} />,
  },
  {
    key: "historico",
    label: "Histórico",
    path: `${BASE_PATH}/historico`,
    icon: <History size={14} />,
  },
];

const initialFilters: ColumnFilters = {
  documento: "",
  categoria: "",
  orgaoEmissor: "",
  validade: "",
  regra: "",
  responsavel: "",
  statusValidade: "",
  operacional: "",
  anexo: "",
  providencia: "",
};

const initialRegisterForm: RegisterDocumentForm = {
  nome: "",
  categoria: "",
  orgaoEmissor: "",
  descricao: "",

  empresaAindaNaoPossuiDocumento: false,
  origemSolicitacao: "cliente",
  clienteSolicitante: "",
  motivoExigencia: "",

  tipoValidade: "com_vencimento",
  periodicidade: "sob_demanda",

  dataEmissao: "",
  dataValidade: "",

  areaResponsavel: "",
  responsavelNome: "",

  providenciaStatus: "sem_agendamento",
  providenciaAgendadaPara: "",
  providenciaTipoAtendimento: "nao_definido",
  providenciaLocal: "",
  providenciaDocumentosNecessarios: "",
  providenciaObservacao: "",

  uploadObservacao: "",
  file: null,
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function normalizeApiDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    const [, year, month, day] = isoDate;
    return `${day}/${month}/${year}`;
  }

  return raw;
}

function normalizeApiDateTime(value: string | null | undefined): string | null {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  if (iso) {
    const [, yyyy, mm, dd, hh, min] = iso;
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  return normalizeApiDate(raw);
}

function parseDateFlexible(value: string | null): Date | null {
  if (!value) return null;

  const raw = String(value).trim();

  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) {
    const [, dd, mm, yyyy] = br;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, yyyy, mm, dd] = iso;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatMesesDias(totalDias: number) {
  const diasAbsolutos = Math.abs(totalDias);
  if (diasAbsolutos < 30) return null;

  const meses = Math.floor(diasAbsolutos / 30);
  const dias = diasAbsolutos % 30;

  const mesesTexto = meses === 1 ? "1 mês" : `${meses} meses`;
  const diasTexto = dias === 1 ? "1 dia" : `${dias} dias`;

  if (dias === 0) return mesesTexto;

  return `${mesesTexto} e ${diasTexto}`;
}

function getPrazoOperacional(item: CertidaoItem) {
  if (!item.documentoPossuido) return "Documento ainda não obtido";

  if (item.tipoValidade === "sem_vencimento") return "Sem vencimento";
  if (item.tipoValidade === "sob_demanda") return "Sob demanda";

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseDateFlexible(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) return "Validade não informada";

  const mesesDias = formatMesesDias(dias);
  const complemento = mesesDias ? ` (${mesesDias})` : "";

  if (dias > 1) return `Faltam ${dias} dias${complemento}`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias${complemento}`;
}

function getValidadePrincipalLabel(item: CertidaoItem) {
  if (!item.documentoPossuido) return "Não aplicável";

  if (item.tipoValidade === "sem_vencimento") return "Sem vencimento";
  if (item.tipoValidade === "sob_demanda") return "Sob demanda";
  if (item.periodicidade === "sem_validade") return "Sem validade";

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  return item.dataValidade || "Validade não cadastrada";
}

function getEmissaoMetaLabel(item: CertidaoItem) {
  if (!item.documentoPossuido) return null;
  if (!item.dataEmissao) return null;

  return `Emissão: ${item.dataEmissao}`;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "canal" : "canais";
}

function normalizeTipoValidade(value: unknown): TipoValidade {
  const raw = String(value || "").trim();

  if (
    raw === "com_vencimento" ||
    raw === "sem_vencimento" ||
    raw === "sob_demanda" ||
    raw === "revisao_periodica"
  ) {
    return raw;
  }

  return "com_vencimento";
}

function normalizePeriodicidade(value: unknown): Periodicidade {
  const raw = String(value || "").trim();

  if (
    raw === "semanal" ||
    raw === "mensal" ||
    raw === "trimestral" ||
    raw === "semestral" ||
    raw === "anual" ||
    raw === "sem_validade" ||
    raw === "sob_demanda"
  ) {
    return raw;
  }

  return "sob_demanda";
}

function normalizeStatusValidade(value: unknown): StatusValidade {
  const raw = String(value || "").trim();

  if (
    raw === "regular" ||
    raw === "a_vencer" ||
    raw === "vencido" ||
    raw === "sem_validade"
  ) {
    return raw;
  }

  return "sem_validade";
}

function normalizeOrigemSolicitacao(value: unknown): OrigemSolicitacao {
  const raw = String(value || "").trim();

  if (
    raw === "cliente" ||
    raw === "portal_cliente" ||
    raw === "email_cliente" ||
    raw === "fiscalizacao" ||
    raw === "contador" ||
    raw === "interno" ||
    raw === "outro"
  ) {
    return raw;
  }

  return "interno";
}

function normalizeProvidenciaStatus(value: unknown): ProvidenciaStatus {
  const raw = String(value || "").trim();

  if (
    raw === "nao_aplicavel" ||
    raw === "nao_iniciado" ||
    raw === "sem_agendamento" ||
    raw === "agendada" ||
    raw === "vistoria_tecnica_agendada" ||
    raw === "comparecimento_presencial" ||
    raw === "aguardando_documentos" ||
    raw === "aguardando_cliente" ||
    raw === "em_emissao" ||
    raw === "em_renovacao" ||
    raw === "emitido" ||
    raw === "concluida"
  ) {
    return raw;
  }

  return "sem_agendamento";
}

function normalizeProvidenciaTipoAtendimento(
  value: unknown
): ProvidenciaTipoAtendimento {
  const raw = String(value || "").trim();

  if (
    raw === "nao_definido" ||
    raw === "online" ||
    raw === "email" ||
    raw === "presencial" ||
    raw === "vistoria_tecnica" ||
    raw === "contador" ||
    raw === "telefone" ||
    raw === "outro"
  ) {
    return raw;
  }

  return "nao_definido";
}

function getOrigemSolicitacaoLabel(origem: OrigemSolicitacao) {
  switch (origem) {
    case "cliente":
      return "Cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "email_cliente":
      return "E-mail do cliente";
    case "fiscalizacao":
      return "Fiscalização";
    case "contador":
      return "Contador";
    case "interno":
      return "Interno";
    case "outro":
      return "Outro";
    default:
      return "Não informada";
  }
}

function getProvidenciaStatusLabel(
  status: ProvidenciaStatus,
  documentoPossuido: boolean
) {
  if (!documentoPossuido) {
    switch (status) {
      case "nao_iniciado":
      case "sem_agendamento":
        return "Não iniciado";
      case "vistoria_tecnica_agendada":
        return "Vistoria técnica agendada";
      case "agendada":
        return "Emissão agendada";
      case "comparecimento_presencial":
        return "Comparecimento presencial";
      case "aguardando_documentos":
        return "Aguardando documentos";
      case "aguardando_cliente":
        return "Aguardando cliente";
      case "em_emissao":
      case "em_renovacao":
        return "Em emissão";
      case "emitido":
      case "concluida":
        return "Emitido";
      case "nao_aplicavel":
        return "Não aplicável";
      default:
        return "Não iniciado";
    }
  }

  switch (status) {
    case "nao_aplicavel":
      return "Não aplicável";
    case "nao_iniciado":
    case "sem_agendamento":
      return "Sem agendamento";
    case "agendada":
      return "Agendada";
    case "vistoria_tecnica_agendada":
      return "Vistoria técnica agendada";
    case "comparecimento_presencial":
      return "Comparecimento presencial";
    case "aguardando_documentos":
      return "Aguardando documentos";
    case "aguardando_cliente":
      return "Aguardando cliente";
    case "em_emissao":
    case "em_renovacao":
      return "Em renovação";
    case "emitido":
    case "concluida":
      return "Concluída";
    default:
      return "Sem agendamento";
  }
}

function getProvidenciaTipoAtendimentoLabel(tipo: ProvidenciaTipoAtendimento) {
  switch (tipo) {
    case "nao_definido":
      return "Não definido";
    case "online":
      return "Online";
    case "email":
      return "E-mail";
    case "presencial":
      return "Presencial";
    case "vistoria_tecnica":
      return "Vistoria técnica";
    case "contador":
      return "Contador";
    case "telefone":
      return "Telefone";
    case "outro":
      return "Outro";
    default:
      return "Não definido";
  }
}

function getProvidenciaVisual(status: ProvidenciaStatus) {
  switch (status) {
    case "agendada":
    case "vistoria_tecnica_agendada":
    case "comparecimento_presencial":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
      };
    case "aguardando_documentos":
    case "aguardando_cliente":
    case "em_emissao":
    case "em_renovacao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
      };
    case "emitido":
    case "concluida":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
      };
    case "nao_iniciado":
    case "nao_aplicavel":
    case "sem_agendamento":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
      };
  }
}

function shouldShowProvidenciaDate(status: ProvidenciaStatus) {
  return (
    status === "agendada" ||
    status === "vistoria_tecnica_agendada" ||
    status === "comparecimento_presencial" ||
    status === "aguardando_documentos" ||
    status === "aguardando_cliente" ||
    status === "em_emissao" ||
    status === "em_renovacao"
  );
}

function mapApiItemToCertidaoItem(
  item: ApiDocumentoItem,
  sessaoEmpresaNome?: string | null,
  sessaoEmpresaCnpj?: string | null
): CertidaoItem {
  const portais = item.portais || {};

  const arquivoVigenteId = item.arquivoVigenteId ?? item.arquivo_vigente_id;
  const anexoDisponivel =
    item.anexoDisponivel ?? item.anexo_disponivel ?? Boolean(arquivoVigenteId);

  const documentoPossuido =
    item.documentoPossuido ??
    item.documento_possuido ??
    Boolean(anexoDisponivel);

  const providenciaStatus =
    item.providenciaStatus ??
    item.providencia_status ??
    item.emissaoStatus ??
    item.emissao_status ??
    item.renovacaoStatus ??
    item.renovacao_status;

  const providenciaAgendadaPara =
    item.providenciaAgendadaPara ??
    item.providencia_agendada_para ??
    item.emissaoAgendadaPara ??
    item.emissao_agendada_para ??
    item.renovacaoAgendadaPara ??
    item.renovacao_agendada_para;

  const providenciaTipoAtendimento =
    item.providenciaTipoAtendimento ??
    item.providencia_tipo_atendimento ??
    item.emissaoTipoAtendimento ??
    item.emissao_tipo_atendimento ??
    item.renovacaoTipoAtendimento ??
    item.renovacao_tipo_atendimento;

  const providenciaLocal =
    item.providenciaLocal ??
    item.providencia_local ??
    item.emissaoLocal ??
    item.emissao_local ??
    item.renovacaoLocal ??
    item.renovacao_local;

  const providenciaDocumentosNecessarios =
    item.providenciaDocumentosNecessarios ??
    item.providencia_documentos_necessarios ??
    item.emissaoDocumentosNecessarios ??
    item.emissao_documentos_necessarios ??
    item.renovacaoDocumentosNecessarios ??
    item.renovacao_documentos_necessarios;

  const providenciaObservacao =
    item.providenciaObservacao ??
    item.providencia_observacao ??
    item.emissaoObservacao ??
    item.emissao_observacao ??
    item.renovacaoObservacao ??
    item.renovacao_observacao;

  return {
    id: Number(item.id),
    nome: toNullableString(item.nome) || "Documento sem nome",
    categoria: toNullableString(item.categoria) || "Sem categoria",
    orgaoEmissor:
      toNullableString(item.orgaoEmissor) ||
      toNullableString(item.orgao_emissor) ||
      "Não informado",

    empresa: sessaoEmpresaNome || "Empresa não informada",
    cnpj: sessaoEmpresaCnpj || "",

    dataEmissao: normalizeApiDate(item.dataEmissao ?? item.data_emissao),
    dataValidade: normalizeApiDate(item.dataValidade ?? item.data_validade),
    dataUpload: normalizeApiDate(item.dataUpload ?? item.data_upload),

    tipoValidade: normalizeTipoValidade(
      item.tipoValidade ?? item.tipo_validade
    ),
    periodicidade: normalizePeriodicidade(item.periodicidade),

    areaResponsavel:
      toNullableString(item.areaResponsavel) ||
      toNullableString(item.area_responsavel) ||
      "Não informado",
    responsavelNome:
      toNullableString(item.responsavelNome) ||
      toNullableString(item.responsavel_nome),

    statusValidade: normalizeStatusValidade(
      item.statusValidade ?? item.status_validade
    ),

    documentoPossuido: Boolean(documentoPossuido),
    origemSolicitacao: normalizeOrigemSolicitacao(
      item.origemSolicitacao ?? item.origem_solicitacao
    ),
    clienteSolicitante:
      toNullableString(item.clienteSolicitante) ||
      toNullableString(item.cliente_solicitante),
    motivoExigencia:
      toNullableString(item.motivoExigencia) ||
      toNullableString(item.motivo_exigencia),

    providenciaStatus: normalizeProvidenciaStatus(providenciaStatus),
    providenciaAgendadaPara: normalizeApiDateTime(providenciaAgendadaPara),
    providenciaTipoAtendimento: normalizeProvidenciaTipoAtendimento(
      providenciaTipoAtendimento
    ),
    providenciaLocal: toNullableString(providenciaLocal),
    providenciaDocumentosNecessarios: toNullableString(
      providenciaDocumentosNecessarios
    ),
    providenciaObservacao: toNullableString(providenciaObservacao),

    anexoDisponivel: Boolean(anexoDisponivel),
    arquivoVigenteId:
      arquivoVigenteId === undefined || arquivoVigenteId === null
        ? null
        : Number(arquivoVigenteId),
    arquivoNome:
      toNullableString(item.arquivoNome) || toNullableString(item.arquivo_nome),
    s3Key: toNullableString(item.s3Key) || toNullableString(item.s3_key),

    observacao:
      toNullableString(item.observacao) || toNullableString(item.descricao),

    versao: 0,
    vigente: Boolean(arquivoVigenteId),
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,

    portais: {
      totalExigem: toNumber(portais.totalExigem),
      anexadosAtualizados: toNumber(portais.anexadosAtualizados),
      pendenteAnexo: toNumber(portais.pendenteAnexo),
      vencidos: toNumber(portais.vencidos),
      renovacaoPendente: toNumber(
        portais.renovacaoPendente ?? portais.emRenovacao
      ),
      aguardandoValidacao: toNumber(portais.aguardandoValidacao),
    },
  };
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  if (!portais.totalExigem || portais.totalExigem <= 0) {
    return {
      key: "nao_exigido",
      label: "Não vinculado",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.renovacaoPendente > 0) {
    return {
      key: "renovacao_pendente",
      label: `Renovação pendente em ${portais.renovacaoPendente} ${pluralPortal(
        portais.renovacaoPendente
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (portais.anexadosAtualizados >= portais.totalExigem) {
    return {
      key: "ok",
      label: `Atualizado em ${portais.totalExigem} ${pluralPortal(
        portais.totalExigem
      )}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = Math.max(
    portais.totalExigem - portais.anexadosAtualizados,
    0
  );

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function hasPendenciaOperacional(item: CertidaoItem) {
  const p = item.portais;

  return (
    !item.documentoPossuido ||
    item.providenciaStatus === "agendada" ||
    item.providenciaStatus === "vistoria_tecnica_agendada" ||
    item.providenciaStatus === "comparecimento_presencial" ||
    item.providenciaStatus === "aguardando_documentos" ||
    item.providenciaStatus === "aguardando_cliente" ||
    item.providenciaStatus === "em_emissao" ||
    item.providenciaStatus === "em_renovacao" ||
    p.vencidos > 0 ||
    p.pendenteAnexo > 0 ||
    p.renovacaoPendente > 0 ||
    p.aguardandoValidacao > 0 ||
    p.anexadosAtualizados < p.totalExigem ||
    !item.anexoDisponivel
  );
}

function getSectionFromPath(pathname: string): SectionKey {
  const path = pathname.toLowerCase();

  if (path.includes("a-vencer")) return "a-vencer";
  if (path.includes("vencidas")) return "vencidas";
  if (path.includes("anexados")) return "anexados";
  if (path.includes("renovacoes")) return "renovacoes";
  if (path.includes("historico") || path.includes("evidencias"))
    return "historico";

  return "visao-geral";
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "semanal":
      return "Semanal";
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
  }
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "a-vencer":
      return "Certidões a vencer";
    case "vencidas":
      return "Certidões vencidas";
    case "anexados":
      return "Documentos anexados";
    case "renovacoes":
      return "Emissões, renovações e providências";
    case "historico":
      return "Histórico e evidências";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

function getUniqueOptions(
  items: CertidaoItem[],
  getter: (item: CertidaoItem) => string
) {
  return Array.from(
    new Set(
      items
        .map(getter)
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function getOperacionalFilterLabel(key: OperacionalKey) {
  switch (key) {
    case "ok":
      return "Atualizado";
    case "pendente_anexo":
      return "Pendente anexo";
    case "vencida":
      return "Vencida";
    case "renovacao_pendente":
      return "Renovação pendente";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "pendente":
      return "Pendente";
    case "nao_exigido":
      return "Não exigido";
    default:
      return "Indefinido";
  }
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  const direct =
    sessao?.modulo_id ??
    sessao?.moduloId ??
    sessao?.modulo_atual_id ??
    sessao?.moduloSelecionadoId ??
    sessao?.modulo?.id;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const isGovernancaDepartment = (dep: any) => {
    const depSlug = normalizeText(dep?.slug);
    const depNome = normalizeText(dep?.nome);
    const depRouteKey = normalizeText(dep?.routeKey ?? dep?.route_key);
    const depCodigo = normalizeText(dep?.codigo);

    return (
      depSlug.includes("governanca") ||
      depSlug.includes("governança") ||
      depNome.includes("governanca") ||
      depNome.includes("governança") ||
      depNome.includes("compliance") ||
      depRouteKey.includes("governanca") ||
      depRouteKey.includes("governança") ||
      depCodigo.includes("governanca") ||
      depCodigo.includes("governança")
    );
  };

  const isCertidoesModule = (mod: any) => {
    const id = mod?.id;

    if (id === undefined || id === null || id === "") return false;

    const full = [
      mod?.codigo,
      mod?.label,
      mod?.nome,
      mod?.routeKey,
      mod?.route_key,
      mod?.actionKey,
      mod?.action_key,
    ]
      .map(normalizeText)
      .join(" ");

    return (
      full.includes("certidoes") ||
      full.includes("certidões") ||
      full.includes("documentos") ||
      full.includes("certidoes-e-documentos") ||
      full.includes("certidoes_documentos")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];
      const moduloCertidoes =
        modulos.find((mod: any) => isCertidoesModule(mod)) || modulos[0];

      const id = moduloCertidoes?.id;

      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);
  if (fromDepartamentosDiretos) return fromDepartamentosDiretos;

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );

    if (fromEnderecoSelecionado) return fromEnderecoSelecionado;
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);
    if (fromEndereco) return fromEndereco;
  }

  return null;
}

const FilterInput: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, onChange }) => {
  return (
    <input
      value={value}
      placeholder={placeholder || "Filtrar"}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterInput}
    />
  );
};

const FilterSelect: React.FC<{
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ value, placeholder, options, onChange }) => {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterSelect}
    >
      <option value="">{placeholder || "Todos"}</option>

      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const CertidoesDocumentosLayout: React.FC<CertidoesDocumentosLayoutProps> = ({
  section,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { sessao } = useAuth() as any;
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const [filters, setFilters] = useState<ColumnFilters>(initialFilters);
  const [documentos, setDocumentos] = useState<CertidaoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerForm, setRegisterForm] =
    useState<RegisterDocumentForm>(initialRegisterForm);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDocumentoId, setDetailDocumentoId] = useState<number | null>(
    null
  );

  const activeSection = section || getSectionFromPath(location.pathname);

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);

  const empresaNome =
    sessao?.empresa?.razao_social ||
    sessao?.empresa_razao_social ||
    "Empresa não informada";

  const empresaCnpj = sessao?.empresa?.cnpj || sessao?.empresa_cnpj || "";

  const updateFilter = (key: keyof ColumnFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateRegisterForm = <K extends keyof RegisterDocumentForm>(
    key: K,
    value: RegisterDocumentForm[K]
  ) => {
    setRegisterForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openRegisterOverlay = () => {
    setRegisterError(null);
    setRegisterOpen(true);
  };

  const closeRegisterOverlay = () => {
    if (registerSaving) return;

    setRegisterOpen(false);
    setRegisterError(null);
  };

  const resetRegisterForm = () => {
    setRegisterForm(initialRegisterForm);
  };

  const reloadDocumentos = () => {
    setReloadKey((prev) => prev + 1);
  };

  const abrirDetalheDocumento = (documentoId: number) => {
    const id = Number(documentoId);

    if (!Number.isFinite(id)) return;

    setDetailDocumentoId(id);
    setDetailOpen(true);
  };

  const fecharDetalheDocumento = () => {
    setDetailOpen(false);
    setDetailDocumentoId(null);
  };

  const handleRegisterDocument = async () => {
    if (registerSaving) return;

    setRegisterError(null);

    try {
      if (!empresaId) {
        throw new Error(
          "empresa_id não encontrado no contexto de autenticação."
        );
      }

      if (!moduloId) {
        throw new Error(
          "modulo_id não encontrado no contexto. Verifique o vínculo do módulo no menu/permissões."
        );
      }

      const nomeFinal = String(registerForm.nome ?? "").trim();
      const categoriaFinal = String(registerForm.categoria ?? "").trim();
      const documentoPossuido = !registerForm.empresaAindaNaoPossuiDocumento;

      if (!nomeFinal) throw new Error("Informe o nome do documento.");
      if (!categoriaFinal) throw new Error("Informe a categoria do documento.");

      const validadeObrigatoria =
        documentoPossuido &&
        (registerForm.tipoValidade === "com_vencimento" ||
          registerForm.tipoValidade === "revisao_periodica");

      if (
        validadeObrigatoria &&
        !String(registerForm.dataValidade ?? "").trim()
      ) {
        throw new Error("Informe a data de vencimento do documento.");
      }

      setRegisterSaving(true);

      const payload = {
        empresa_id: empresaId,
        modulo_id: moduloId,

        nome: nomeFinal,
        categoria: categoriaFinal,
        orgao_emissor: String(registerForm.orgaoEmissor ?? "").trim() || null,
        descricao: String(registerForm.descricao ?? "").trim() || null,

        documento_possuido: documentoPossuido,
        origem_solicitacao: registerForm.origemSolicitacao,
        cliente_solicitante:
          String(registerForm.clienteSolicitante ?? "").trim() || null,
        motivo_exigencia:
          String(registerForm.motivoExigencia ?? "").trim() || null,

        tipo_validade: documentoPossuido
          ? registerForm.tipoValidade
          : "sob_demanda",
        periodicidade: documentoPossuido
          ? registerForm.periodicidade
          : "sob_demanda",

        data_emissao: documentoPossuido
          ? registerForm.dataEmissao || null
          : null,
        data_validade:
          documentoPossuido &&
          registerForm.tipoValidade !== "sem_vencimento" &&
          registerForm.tipoValidade !== "sob_demanda"
            ? registerForm.dataValidade || null
            : null,

        area_responsavel:
          String(registerForm.areaResponsavel ?? "").trim() || null,
        responsavel_nome:
          String(registerForm.responsavelNome ?? "").trim() || null,

        providencia_status: registerForm.providenciaStatus,
        providencia_agendada_para: shouldShowProvidenciaDate(
          registerForm.providenciaStatus
        )
          ? String(registerForm.providenciaAgendadaPara ?? "").trim() || null
          : null,
        providencia_tipo_atendimento: registerForm.providenciaTipoAtendimento,
        providencia_local:
          String(registerForm.providenciaLocal ?? "").trim() || null,
        providencia_documentos_necessarios:
          String(registerForm.providenciaDocumentosNecessarios ?? "").trim() ||
          null,
        providencia_observacao:
          String(registerForm.providenciaObservacao ?? "").trim() || null,

        emissao_status: !documentoPossuido
          ? registerForm.providenciaStatus
          : null,
        emissao_agendada_para:
          !documentoPossuido &&
          shouldShowProvidenciaDate(registerForm.providenciaStatus)
            ? String(registerForm.providenciaAgendadaPara ?? "").trim() || null
            : null,
        emissao_tipo_atendimento: !documentoPossuido
          ? registerForm.providenciaTipoAtendimento
          : null,
        emissao_local: !documentoPossuido
          ? String(registerForm.providenciaLocal ?? "").trim() || null
          : null,
        emissao_documentos_necessarios: !documentoPossuido
          ? String(
              registerForm.providenciaDocumentosNecessarios ?? ""
            ).trim() || null
          : null,
        emissao_observacao: !documentoPossuido
          ? String(registerForm.providenciaObservacao ?? "").trim() || null
          : null,

        renovacao_status: documentoPossuido
          ? registerForm.providenciaStatus
          : null,
        renovacao_agendada_para:
          documentoPossuido &&
          shouldShowProvidenciaDate(registerForm.providenciaStatus)
            ? String(registerForm.providenciaAgendadaPara ?? "").trim() || null
            : null,
        renovacao_tipo_atendimento: documentoPossuido
          ? registerForm.providenciaTipoAtendimento
          : null,
        renovacao_local: documentoPossuido
          ? String(registerForm.providenciaLocal ?? "").trim() || null
          : null,
        renovacao_documentos_necessarios: documentoPossuido
          ? String(
              registerForm.providenciaDocumentosNecessarios ?? ""
            ).trim() || null
          : null,
        renovacao_observacao: documentoPossuido
          ? String(registerForm.providenciaObservacao ?? "").trim() || null
          : null,
      };

      const documentoResponse = await fetch(API_DOCUMENTOS_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const documentoData = (await documentoResponse
        .json()
        .catch(() => null)) as ApiPostDocumentoResponse | null;

      if (!documentoResponse.ok || !documentoData?.ok) {
        throw new Error(
          documentoData?.erro ||
            `Falha ao registrar documento. Status HTTP ${documentoResponse.status}`
        );
      }

      const documentoId = Number(documentoData?.documento?.id);

      if (!Number.isFinite(documentoId)) {
        throw new Error(
          "Documento criado, mas o ID não foi retornado pela API."
        );
      }

      if (documentoPossuido && registerForm.file) {
        const file = registerForm.file;

        const presignResponse = await fetch(API_PRESIGN_UPLOAD_URL, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            empresa_id: empresaId,
            modulo_id: moduloId,
            documento_id: documentoId,
            mime_type: file.type || "application/octet-stream",
            original_name: file.name,
            size_bytes: file.size,
          }),
        });

        const presignData = (await presignResponse
          .json()
          .catch(() => null)) as ApiPresignUploadResponse | null;

        if (!presignResponse.ok || !presignData?.ok || !presignData.uploadUrl) {
          throw new Error(
            presignData?.erro ||
              `Documento criado, mas falhou ao preparar upload. Status HTTP ${presignResponse.status}`
          );
        }

        const uploadResponse = await fetch(presignData.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Documento criado, mas falhou ao enviar arquivo ao S3. Status HTTP ${uploadResponse.status}`
          );
        }

        const registrarResponse = await fetch(API_REGISTRAR_ANEXO_URL, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            empresa_id: empresaId,
            modulo_id: moduloId,
            documento_id: documentoId,
            s3_bucket: presignData.bucket,
            s3_key: presignData.key,
            mime_type: file.type || "application/octet-stream",
            original_name: file.name,
            size_bytes: file.size,
            data_emissao: registerForm.dataEmissao || null,
            data_validade:
              registerForm.tipoValidade === "sem_vencimento" ||
              registerForm.tipoValidade === "sob_demanda"
                ? null
                : registerForm.dataValidade || null,
            observacao:
              String(registerForm.uploadObservacao ?? "").trim() || null,
          }),
        });

        const registrarData = (await registrarResponse
          .json()
          .catch(() => null)) as ApiRegistrarAnexoResponse | null;

        if (!registrarResponse.ok || !registrarData?.ok) {
          throw new Error(
            registrarData?.erro ||
              `Documento criado, mas falhou ao registrar o anexo. Status HTTP ${registrarResponse.status}`
          );
        }
      }

      resetRegisterForm();
      setRegisterOpen(false);
      reloadDocumentos();
    } catch (error: any) {
      setRegisterError(error?.message || "Falha ao registrar documento.");
    } finally {
      setRegisterSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const carregarDocumentos = async () => {
      setLoading(true);
      setErro(null);

      try {
        if (!empresaId) {
          throw new Error(
            "empresa_id não encontrado no contexto de autenticação."
          );
        }

        if (!moduloId) {
          throw new Error(
            "modulo_id não encontrado no contexto. Verifique se o módulo Certidões e Documentos está vinculado ao departamento Governança no menu/permissões."
          );
        }

        const params = new URLSearchParams();

        params.set("section", activeSection);
        params.set("empresa_id", String(empresaId));
        params.set("modulo_id", String(moduloId));

        const url = `${API_DOCUMENTOS_URL}?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        });

        const data = (await response
          .json()
          .catch(() => null)) as ApiDocumentosResponse | null;

        if (!response.ok || !data?.ok) {
          throw new Error(
            data?.erro ||
              `Falha ao carregar documentos. Status HTTP ${response.status}`
          );
        }

        const items = Array.isArray(data.items) ? data.items : [];

        const mapped = items.map((item) =>
          mapApiItemToCertidaoItem(item, empresaNome, empresaCnpj)
        );

        if (!cancelled) setDocumentos(mapped);
      } catch (error: any) {
        if (!cancelled) {
          setErro(error?.message || "Falha ao carregar documentos.");
          setDocumentos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    carregarDocumentos();

    return () => {
      cancelled = true;
    };
  }, [activeSection, empresaId, moduloId, empresaNome, empresaCnpj, reloadKey]);

  const filteredBySection = useMemo(() => {
    if (activeSection === "a-vencer") {
      return documentos.filter((item) => item.statusValidade === "a_vencer");
    }

    if (activeSection === "vencidas") {
      return documentos.filter((item) => item.statusValidade === "vencido");
    }

    if (activeSection === "anexados") {
      return documentos.filter((item) => item.anexoDisponivel);
    }

    if (activeSection === "renovacoes") {
      return documentos.filter(
        (item) =>
          !item.documentoPossuido ||
          item.providenciaStatus === "agendada" ||
          item.providenciaStatus === "vistoria_tecnica_agendada" ||
          item.providenciaStatus === "comparecimento_presencial" ||
          item.providenciaStatus === "aguardando_documentos" ||
          item.providenciaStatus === "aguardando_cliente" ||
          item.providenciaStatus === "em_emissao" ||
          item.providenciaStatus === "em_renovacao"
      );
    }

    if (activeSection === "historico") {
      return documentos.filter(
        (item) =>
          item.obsoleto ||
          item.versao > 1 ||
          hasPendenciaOperacional(item) ||
          item.anexoDisponivel
      );
    }

    return documentos;
  }, [activeSection, documentos]);

  const filterOptions = useMemo(() => {
    const categorias = getUniqueOptions(
      filteredBySection,
      (item) => item.categoria
    );
    const orgaos = getUniqueOptions(
      filteredBySection,
      (item) => item.orgaoEmissor
    );
    const responsaveis = getUniqueOptions(
      filteredBySection,
      (item) => item.areaResponsavel
    );

    const regras = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.tipoValidade,
          getTipoValidadeLabel(item.tipoValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const statusValidade = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.statusValidade,
          getStatusValidadeLabel(item.statusValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const operacional = Array.from(
      new Map(
        filteredBySection.map((item) => {
          const visual = getOperacionalVisual(item.portais);
          return [visual.key, getOperacionalFilterLabel(visual.key)];
        })
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    return {
      categorias: categorias.map((value) => ({ value, label: value })),
      orgaos: orgaos.map((value) => ({ value, label: value })),
      regras,
      responsaveis: responsaveis.map((value) => ({ value, label: value })),
      statusValidade,
      operacional,
      anexos: [
        { value: "vinculado", label: "Vinculado" },
        { value: "pendente", label: "Pendente" },
      ],
      providencias: [
        { value: "nao_iniciado", label: "Não iniciado" },
        { value: "sem_agendamento", label: "Sem agendamento" },
        {
          value: "vistoria_tecnica_agendada",
          label: "Vistoria técnica agendada",
        },
        { value: "agendada", label: "Agendada" },
        {
          value: "comparecimento_presencial",
          label: "Comparecimento presencial",
        },
        { value: "aguardando_documentos", label: "Aguardando documentos" },
        { value: "aguardando_cliente", label: "Aguardando cliente" },
        { value: "em_emissao", label: "Em emissão" },
        { value: "em_renovacao", label: "Em renovação" },
        { value: "emitido", label: "Emitido" },
        { value: "concluida", label: "Concluída" },
        { value: "nao_aplicavel", label: "Não aplicável" },
      ],
    };
  }, [filteredBySection]);

  const filteredItems = useMemo(() => {
    return filteredBySection.filter((item) => {
      const operacional = getOperacionalVisual(item.portais);

      const documentoSearch = normalizeText(filters.documento);
      const validadeSearch = normalizeText(filters.validade);

      const documentoMatch =
        !documentoSearch ||
        [
          item.nome,
          item.cnpj,
          item.empresa,
          item.arquivoNome || "",
          item.s3Key || "",
          item.clienteSolicitante || "",
          item.motivoExigencia || "",
          item.providenciaLocal || "",
          item.providenciaDocumentosNecessarios || "",
          item.providenciaObservacao || "",
          item.obsoleto ? "obsoleto" : "",
          !item.documentoPossuido ? "nao possuido documento ainda nao tem" : "",
        ].some((field) => normalizeText(field).includes(documentoSearch));

      const categoriaMatch =
        !filters.categoria || item.categoria === filters.categoria;

      const orgaoMatch =
        !filters.orgaoEmissor || item.orgaoEmissor === filters.orgaoEmissor;

      const validadeMatch =
        !validadeSearch ||
        [
          getValidadePrincipalLabel(item),
          getPrazoOperacional(item),
          getEmissaoMetaLabel(item) || "",
          item.providenciaAgendadaPara || "",
        ].some((field) => normalizeText(field).includes(validadeSearch));

      const regraMatch = !filters.regra || item.tipoValidade === filters.regra;

      const responsavelMatch =
        !filters.responsavel || item.areaResponsavel === filters.responsavel;

      const statusValidadeMatch =
        !filters.statusValidade ||
        item.statusValidade === filters.statusValidade;

      const operacionalMatch =
        !filters.operacional || operacional.key === filters.operacional;

      const anexoValue: AnexoFilter = item.anexoDisponivel
        ? "vinculado"
        : "pendente";

      const anexoMatch = !filters.anexo || anexoValue === filters.anexo;
      const providenciaMatch =
        !filters.providencia || item.providenciaStatus === filters.providencia;

      return (
        documentoMatch &&
        categoriaMatch &&
        orgaoMatch &&
        validadeMatch &&
        regraMatch &&
        responsavelMatch &&
        statusValidadeMatch &&
        operacionalMatch &&
        anexoMatch &&
        providenciaMatch
      );
    });
  }, [filteredBySection, filters]);

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Certidões e Documentos - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.navigationBar}>
          <nav style={styles.tabs}>
            {sections.map((item) => {
              const active = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.tabButton,
                    ...(active ? styles.tabButtonActive : {}),
                    ...(active
                      ? {
                          background: toRgba(activeDepartmentColor, 0.08),
                          borderColor: activeDepartmentColor,
                          color: activeDepartmentColor,
                        }
                      : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            style={{
              ...styles.registerButton,
              color: "#ffffff",
              borderColor: activeDepartmentColor,
              background: activeDepartmentColor,
            }}
            onClick={openRegisterOverlay}
          >
            <Plus size={14} />
            Registrar documento
          </button>
        </section>

        {(activeSection === "a-vencer" ||
          activeSection === "vencidas" ||
          activeSection === "renovacoes") && (
          <section
            style={{
              ...styles.notice,
              ...(activeSection === "vencidas"
                ? styles.noticeDanger
                : styles.noticeWarning),
            }}
          >
            <div style={styles.noticeIcon}>
              {activeSection === "vencidas" ? (
                <AlertTriangle size={16} />
              ) : (
                <CalendarClock size={16} />
              )}
            </div>

            <div>
              <div style={styles.noticeTitle}>
                {activeSection === "vencidas"
                  ? "Regularização necessária"
                  : activeSection === "renovacoes"
                  ? "Emissões, renovações e providências"
                  : "Renovação preventiva recomendada"}
              </div>

              <div style={styles.noticeText}>
                {activeSection === "vencidas"
                  ? "Existem documentos vencidos. Recomenda-se tratar a regularização como prioridade operacional."
                  : activeSection === "renovacoes"
                  ? "Acompanhe documentos ainda não obtidos, emissões não iniciadas, vistorias técnicas, renovações e demais providências."
                  : "Documentos próximos ao vencimento devem ser acompanhados com antecedência para evitar perda de regularidade."}
              </div>
            </div>
          </section>
        )}

        {erro && (
          <section style={styles.errorBox}>
            <AlertTriangle size={16} />
            <div>
              <strong>Não foi possível carregar os documentos.</strong>
              <span>{erro}</span>
            </div>
          </section>
        )}

        <section style={styles.tablePanel}>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Documento</th>
                  <th style={styles.th}>Categoria</th>
                  <th style={styles.th}>Órgão emissor</th>
                  <th style={styles.th}>Validade</th>
                  <th style={styles.th}>Regra</th>
                  <th style={styles.th}>Responsável</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Providência</th>
                  <th style={styles.th}>Operacional</th>
                  <th style={styles.th}>Anexo</th>
                </tr>

                <tr>
                  <th style={styles.filterTh}>
                    <FilterInput
                      value={filters.documento}
                      placeholder="Documento"
                      onChange={(value) => updateFilter("documento", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.categoria}
                      placeholder="Todos"
                      options={filterOptions.categorias}
                      onChange={(value) => updateFilter("categoria", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.orgaoEmissor}
                      placeholder="Todos"
                      options={filterOptions.orgaos}
                      onChange={(value) => updateFilter("orgaoEmissor", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterInput
                      value={filters.validade}
                      placeholder="Validade"
                      onChange={(value) => updateFilter("validade", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.regra}
                      placeholder="Todos"
                      options={filterOptions.regras}
                      onChange={(value) => updateFilter("regra", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.responsavel}
                      placeholder="Todos"
                      options={filterOptions.responsaveis}
                      onChange={(value) => updateFilter("responsavel", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.statusValidade}
                      placeholder="Todos"
                      options={filterOptions.statusValidade}
                      onChange={(value) =>
                        updateFilter("statusValidade", value)
                      }
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.providencia}
                      placeholder="Todas"
                      options={filterOptions.providencias}
                      onChange={(value) => updateFilter("providencia", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.operacional}
                      placeholder="Todos"
                      options={filterOptions.operacional}
                      onChange={(value) => updateFilter("operacional", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.anexo}
                      placeholder="Todos"
                      options={filterOptions.anexos}
                      onChange={(value) => updateFilter("anexo", value)}
                    />
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={10} style={styles.loadingCell}>
                      <Loader2 size={16} />
                      Carregando documentos...
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredItems.map((item) => (
                    <DocumentRow
                      key={item.id}
                      item={item}
                      departmentColor={activeDepartmentColor}
                      onDoubleClick={() => abrirDetalheDocumento(item.id)}
                    />
                  ))}

                {!loading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={10} style={styles.emptyCell}>
                      Nenhum documento registrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegisterDocumentSidePanel
        open={registerOpen}
        departmentColor={activeDepartmentColor}
        form={registerForm}
        saving={registerSaving}
        error={registerError}
        onClose={closeRegisterOverlay}
        onChange={updateRegisterForm}
        onSubmit={handleRegisterDocument}
      />

      <DocumentoDetailSidePanel
        open={detailOpen}
        documentoId={detailDocumentoId}
        departmentColor={activeDepartmentColor}
        onClose={fecharDetalheDocumento}
        onAfterChange={reloadDocumentos}
      />
    </div>
  );
};

const DocumentRow: React.FC<{
  item: CertidaoItem;
  departmentColor: string;
  onDoubleClick?: () => void;
}> = ({ item, departmentColor, onDoubleClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const statusValidadeVisual = getStatusValidadeVisual(item.statusValidade);
  const operacional = getOperacionalVisual(item.portais);
  const providenciaVisual = getProvidenciaVisual(item.providenciaStatus);

  const rowHoverBackground = toRgba(departmentColor, 0.055);
  const rowHoverBorder = toRgba(departmentColor, 0.22);
  const rowHoverIconBackground = toRgba(departmentColor, 0.08);
  const rowHoverIconBorder = toRgba(departmentColor, 0.28);

  const tdStyle: React.CSSProperties = {
    ...styles.td,
    borderTopColor: isHovered ? rowHoverBorder : "#f0f2f4",
    borderBottomColor: isHovered ? rowHoverBorder : "#f0f2f4",
  };

  return (
    <tr
      style={{
        ...styles.tr,
        background: isHovered ? rowHoverBackground : "#ffffff",
        boxShadow: isHovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={onDoubleClick}
      title="Clique duas vezes para abrir os detalhes do documento"
    >
      <td style={tdStyle}>
        <div style={styles.docCell}>
          <div
            style={{
              ...styles.docIcon,
              ...(isHovered
                ? {
                    background: rowHoverIconBackground,
                    borderColor: rowHoverIconBorder,
                    color: departmentColor,
                  }
                : {}),
            }}
          >
            <FileText size={14} />
          </div>

          <div style={styles.docInfo}>
            <div
              style={{
                ...styles.docName,
                color: isHovered ? departmentColor : "#111827",
              }}
            >
              {item.nome}
            </div>

            <div style={styles.docMeta}>
              {item.documentoPossuido
                ? "Documento registrado"
                : "Ainda não possuído"}
              {item.arquivoNome ? ` • ${item.arquivoNome}` : ""}
              {item.obsoleto ? " • Obsoleto" : ""}
            </div>
          </div>
        </div>
      </td>

      <td style={tdStyle}>{item.categoria}</td>

      <td style={tdStyle}>{item.orgaoEmissor}</td>

      <td style={tdStyle}>
        {item.documentoPossuido ? (
          <>
            <div style={styles.dateText}>{getValidadePrincipalLabel(item)}</div>

            <div
              style={{
                ...styles.prazoText,
                ...(item.statusValidade === "vencido"
                  ? styles.prazoDanger
                  : {}),
                ...(item.statusValidade === "a_vencer"
                  ? styles.prazoWarning
                  : {}),
              }}
            >
              {getPrazoOperacional(item)}
            </div>

            {getEmissaoMetaLabel(item) && (
              <div style={styles.docMeta}>{getEmissaoMetaLabel(item)}</div>
            )}
          </>
        ) : (
          <>
            <div style={styles.prazoDanger}>Não aplicável</div>
            <div style={styles.docMeta}>
              Documento ainda precisa ser emitido/obtido.
            </div>
          </>
        )}
      </td>

      <td style={tdStyle}>
        <div style={styles.ruleText}>
          {item.documentoPossuido
            ? getTipoValidadeLabel(item.tipoValidade)
            : "A emitir"}
        </div>

        <div style={styles.docMeta}>
          {item.documentoPossuido
            ? getPeriodicidadeLabel(item.periodicidade)
            : "Sem validade registrada"}
        </div>
      </td>

      <td style={tdStyle}>
        <div style={styles.responsavelArea}>{item.areaResponsavel}</div>

        {item.responsavelNome && (
          <div style={styles.docMeta}>{item.responsavelNome}</div>
        )}
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: item.documentoPossuido
              ? statusValidadeVisual.background
              : "#fff7ed",
            color: item.documentoPossuido
              ? statusValidadeVisual.color
              : "#9a3412",
            borderColor: item.documentoPossuido
              ? statusValidadeVisual.border
              : "#fed7aa",
          }}
        >
          {item.documentoPossuido ? (
            statusValidadeVisual.icon
          ) : (
            <Clock3 size={12} />
          )}
          {item.documentoPossuido
            ? getStatusValidadeLabel(item.statusValidade)
            : "A emitir"}
        </span>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: providenciaVisual.background,
            color: providenciaVisual.color,
            borderColor: providenciaVisual.border,
          }}
        >
          <CalendarClock size={12} />
          {getProvidenciaStatusLabel(
            item.providenciaStatus,
            item.documentoPossuido
          )}
        </span>

        <div style={styles.docMeta}>
          {item.providenciaAgendadaPara || "Sem data agendada"}
        </div>

        <div style={styles.docMeta}>
          {getProvidenciaTipoAtendimentoLabel(item.providenciaTipoAtendimento)}
          {item.providenciaLocal ? ` • ${item.providenciaLocal}` : ""}
        </div>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: operacional.background,
            color: operacional.color,
            borderColor: operacional.border,
          }}
        >
          {operacional.label}
        </span>
      </td>

      <td style={tdStyle}>
        {item.documentoPossuido ? (
          item.anexoDisponivel ? (
            <span style={styles.attachmentOk}>
              <Paperclip size={12} />
              Vinculado
            </span>
          ) : (
            <span style={styles.attachmentPending}>Pendente</span>
          )
        ) : (
          <span style={styles.attachmentPending}>Não possui</span>
        )}
      </td>
    </tr>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  navigationBar: {
    minHeight: 42,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "5px 8px",
  },

  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    overflowX: "auto",
    minWidth: 0,
  },

  tabButton: {
    height: 30,
    padding: "0 10px",
    border: "1px solid transparent",
    background: "transparent",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  tabButtonActive: {
    background: "rgba(47, 63, 88, 0.08)",
    borderColor: DEPARTMENT_COLOR,
    color: DEPARTMENT_COLOR,
  },

  tabIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "inherit",
  },

  registerButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid",
  },

  noticeWarning: {
    background: "#fff8e8",
    borderColor: "#e7cf9a",
    color: "#6b4f1f",
  },

  noticeDanger: {
    background: "#fff1f1",
    borderColor: "#efb4b4",
    color: "#7f1d1d",
  },

  noticeIcon: {
    marginTop: 1,
    display: "inline-flex",
    flexShrink: 0,
  },

  noticeTitle: {
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 2,
  },

  noticeText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
  },

  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 700,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1360,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    width: "8.33%",
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  filterTh: {
    width: "8.33%",
    padding: "5px 6px",
    background: "#ffffff",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
  },

  filterInput: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 7px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterSelect: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 6px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterDisabled: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
    transition:
      "background 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  docCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  docIcon: {
    width: 28,
    height: 28,
    background: "#f2f5f8",
    border: "1px solid #e2e8f0",
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  docInfo: {
    minWidth: 0,
  },

  docName: {
    fontSize: 12.4,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.25,
  },

  docMeta: {
    marginTop: 1,
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    lineHeight: 1.25,
  },

  dateText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#111827",
  },

  prazoText: {
    marginTop: 1,
    fontSize: 11.4,
    fontWeight: 800,
    color: "#166534",
  },

  prazoWarning: {
    color: "#92400e",
  },

  prazoDanger: {
    color: "#991b1b",
  },

  ruleText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#111827",
  },

  responsavelArea: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#111827",
  },

  statusBadge: {
    minHeight: 22,
    padding: "0 7px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  attachmentOk: {
    minHeight: 22,
    padding: "0 7px",
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  attachmentPending: {
    minHeight: 22,
    padding: "0 7px",
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  observacaoText: {
    fontSize: 11.5,
    fontWeight: 700,
    color: "#475569",
    lineHeight: 1.25,
  },

  observacaoDanger: {
    fontSize: 11.5,
    fontWeight: 850,
    color: "#991b1b",
    lineHeight: 1.25,
  },

  loadingCell: {
    height: 68,
    textAlign: "center",
    color: "#475569",
    fontSize: 12.5,
    fontWeight: 750,
  },

  emptyCell: {
    height: 68,
    textAlign: "center",
    color: "#64748b",
    fontSize: 12.5,
    fontWeight: 700,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(1040px, 96vw)",
    maxHeight: "92vh",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.28)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  overlayEyebrow: {
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1.15,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
    maxWidth: 780,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  overlayError: {
    margin: "12px 18px 0",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  formGrid: {
    padding: 18,
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 14px",
  },

  formField: {
    minWidth: 0,
  },

  formFieldWide: {
    gridColumn: "span 2",
    minWidth: 0,
  },

  formFieldFull: {
    gridColumn: "1 / -1",
    minWidth: 0,
  },

  formDivider: {
    marginTop: 4,
    paddingTop: 8,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 950,
  },

  formLabel: {
    display: "block",
    marginBottom: 5,
    color: "#475569",
    fontSize: 10.8,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  formInput: {
    width: "100%",
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextarea: {
    width: "100%",
    minHeight: 82,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "9px 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextareaSmall: {
    width: "100%",
    minHeight: 58,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "9px 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  checkboxLine: {
    minHeight: 32,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "#111827",
    fontSize: 12.3,
    fontWeight: 850,
    cursor: "pointer",
  },

  formHelpText: {
    marginTop: 3,
    color: "#64748b",
    fontSize: 11.4,
    fontWeight: 650,
  },

  uploadBox: {
    minHeight: 62,
    border: "1px dashed #94a3b8",
    background: "#f8fafc",
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },

  uploadBoxIcon: {
    width: 34,
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  uploadBoxText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
  },

  hiddenFileInput: {
    display: "none",
  },

  overlayFooter: {
    padding: "12px 18px 16px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },

  cancelButton: {
    height: 34,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  saveButton: {
    height: 34,
    padding: "0 14px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
};

export default CertidoesDocumentosLayout;
